import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["input", "popover", "geolocator", "displayButton", "displayValue", "expanded"];
  static values = {
    token: String,
  };

  connect() {
    // Start in collapsed state if we have display button
    if (this.hasDisplayButtonTarget) {
      this.updateDisplay();
    }
    // Track the last valid address (selected from results or geolocated)
    this.validAddress = this.inputTarget.value.trim();
  }

  expand() {
    if (!this.hasDisplayButtonTarget) return;

    this.displayButtonTarget.classList.add("hidden");
    this.expandedTarget.classList.remove("hidden");
    this.expandedTarget.classList.add("flex");
    // Focus the input after a brief delay to ensure transition starts
    setTimeout(() => this.inputTarget.focus(), 50);
  }

  collapse(event) {
    if (!this.hasDisplayButtonTarget) return;

    // Delay collapse to check if user clicked results
    this.collapseTimeout = setTimeout(() => {
      const currentValue = this.inputTarget.value.trim();

      if (currentValue === "") {
        // User intentionally cleared the input → clear the valid address
        this.validAddress = "";
      } else {
        // User typed something but didn't select → restore previous valid address
        this.inputTarget.value = this.validAddress || "";
      }

      this.expandedTarget.classList.add("hidden");
      this.expandedTarget.classList.remove("flex");
      this.displayButtonTarget.classList.remove("hidden");
      this.updateDisplay();
      // Hide results when collapsing
      if (this.hasPopoverTarget) {
        this.popoverTarget.classList.add("hidden");
        this.popoverTarget.classList.remove("flex");
      }
    }, 150);
  }

  cancelCollapse() {
    if (this.collapseTimeout) {
      clearTimeout(this.collapseTimeout);
      this.collapseTimeout = null;
    }
  }

  updateDisplay() {
    if (!this.hasDisplayValueTarget) return;

    const value = this.inputTarget.value.trim();

    if (value === "") {
      // No address set - hide the value display completely
      this.displayValueTarget.textContent = "";
      this.displayValueTarget.classList.add("hidden");
    } else {
      // Address is set - show formatted address (zipcode + city)
      const parts = value.split(",").map(s => s.trim());
      const displayText = parts.length >= 2 ? parts[1] : value;

      this.displayValueTarget.textContent = displayText;
      this.displayValueTarget.classList.remove("hidden");
    }
  }

  async search(e) {
    const query = this.inputTarget.value.trim();
    if (query.length < 3) {
      // Hide results if query too short
      if (this.hasPopoverTarget) {
        this.popoverTarget.classList.add("hidden");
        this.popoverTarget.classList.remove("flex");
      }
      return;
    }

    const data = await fetch(
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
        query
      )}.json?access_token=${
        this.tokenValue
      }&autocomplete=true&language=fr&country=fr&types=address&proximity=2.3522,48.8566`
    );

    const json = await data.json();

    if (json && json.features.length > 0) {
      const { features } = json;

      this.popoverTarget.innerHTML = "";

      features.forEach((feature) => {
        const li = document.createElement("li");
        li.textContent = feature.place_name;
        li.className = "px-4 py-3 cursor-pointer text-sm text-[#2e2e2e] border-b border-[#f4f4f4] transition-colors duration-200 last:border-b-0 hover:bg-[#ebe2ff] hover:text-[#9565fe]";
        li.addEventListener("click", (clickEvent) => {
          this.cancelCollapse(); // Cancel any pending collapse
          clickEvent.stopPropagation(); // Prevent closing parent popover
          this.inputTarget.value = feature.place_name;
          this.validAddress = feature.place_name; // Mark as valid selection
          this.popoverTarget.replaceChildren();
          this.popoverTarget.classList.add("hidden");

          // Emit sync event
          window.dispatchEvent(new CustomEvent("search-field:changed", {
            detail: {
              field: "address",
              value: feature.place_name,
              source: this.element
            }
          }));
          this.popoverTarget.classList.remove("flex");

          // If using the mobile component, collapse it
          if (this.hasDisplayButtonTarget) {
            this.collapse();
          }
        });
        this.popoverTarget.appendChild(li);
      });

      // Show results
      this.popoverTarget.classList.remove("hidden");
      this.popoverTarget.classList.add("flex");
    }
  }

  async locate() {
    if (!navigator.geolocation) {
      alert("La géolocalisation n'est pas supportée par ce navigateur.");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;

        const res = await fetch(
          `https://api.mapbox.com/geocoding/v5/mapbox.places/${longitude},${latitude}.json?access_token=${this.tokenValue}&language=fr`
        );
        const json = await res.json();

        if (json && json.features.length > 0) {
          const place = json.features[0].place_name;
          this.inputTarget.value = place;
          this.validAddress = place; // Mark as valid selection
          this.inputTarget.dispatchEvent(new Event("input"));
          this.geolocatorTarget.classList.add("active");
        }
      },
      (error) => {
        console.error("Erreur de géolocalisation :", error);
        alert("Impossible de récupérer votre position.");
      }
    );
  }
}
